import { useCallback } from 'react';
import { LocationData, Server, GameNotification, ServerAgeStage } from '../types';
import { ActiveEvent } from './useRandomEvents';
import {
  SERVER_TYPES,
  SATISFACTION_DECAY_PER_CHECK,
  SATISFACTION_RECOVERY_PER_CHECK,
  SATISFACTION_CHURN_THRESHOLD,
  SATISFACTION_CHURN_INTERVAL_TICKS,
  SERVER_AGING_MATURE_TICKS,
  SERVER_AGING_OLD_TICKS,
  SERVER_AGING_EOL_TICKS,
  SERVER_AGING_EFFICIENCY_DECAY,
  SERVER_AGING_CHECK_INTERVAL,
} from '../constants';

interface UseCustomerSatisfactionProps {
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  isPaused: boolean;
  triggerNotification: (message: string, type: GameNotification['type'], duration?: number) => void;
}

export const useCustomerSatisfaction = ({
  locations,
  setLocations,
  isPaused,
  triggerNotification,
}: UseCustomerSatisfactionProps) => {

  const updateSatisfactionAndAging = useCallback((currentGameTime: number, activeEvents: ActiveEvent[] = []) => {
    if (isPaused) return;

    const churnThisTick = currentGameTime % SATISFACTION_CHURN_INTERVAL_TICKS === 0;
    const agingThisTick = currentGameTime % SERVER_AGING_CHECK_INTERVAL === 0;

    // Heatwave adds extra satisfaction decay for every server in an affected location
    const heatwaveLocationIds = new Set(
      activeEvents.filter(e => e.id === 'heatwave').map(e => e.locationId)
    );
    // DDoS also hurts satisfaction (customer-facing disruption)
    const ddosLocationIds = new Set(
      activeEvents.filter(e => e.id === 'ddosAttack').map(e => e.locationId)
    );
    // Network outage / fiber cut: clients can't reach servers at all — severe decay
    const networkOutageLocationIds = new Set(
      activeEvents.filter(e => e.id === 'networkOutage' || e.id === 'fiberCut').map(e => e.locationId)
    );
    // Power outage: full outage means servers are unreachable — heavy decay
    const powerOutageLocationIds = new Set(
      activeEvents.filter(e => e.id === 'powerOutage').map(e => e.locationId)
    );

    let anyChanged = false;

    setLocations(prev => {
      const next: Record<string, LocationData> = {};
      Object.entries(prev).forEach(([locId, location]) => {
        if (!location.unlocked || !location.floors) {
          next[locId] = location;
          return;
        }
        let locChanged = false;
        const updatedFloors: Record<string, typeof location.floors[string]> = {};

        Object.entries(location.floors).forEach(([floorId, floor]) => {
          let floorChanged = false;
          const updatedRacks = floor.racks.map(rack => {
            let rackChanged = false;
            const updatedServers = rack.servers.map((server: Server) => {
              let s = { ...server };
              let changed = false;

              // ── Satisfaction ─────────────────────────────────────────────
              const currentSatisfaction = s.satisfactionScore ?? 100;
              const isDown = s.failureStatus === 'failed' || s.failureStatus === 'repairing';

              // Extra decay from active events — applied even when the server hardware is fine
              const heatwaveBonus    = heatwaveLocationIds.has(locId)      ? 2 : 0;
              const ddosBonus        = ddosLocationIds.has(locId)           ? 1 : 0;
              // Network outage / fiber cut: clients can't reach anything — as bad as the server being down
              const networkBonus     = networkOutageLocationIds.has(locId)  ? SATISFACTION_DECAY_PER_CHECK + 2 : 0;
              // Power outage: complete service loss, heavy decay
              const powerBonus       = powerOutageLocationIds.has(locId)    ? SATISFACTION_DECAY_PER_CHECK + 1 : 0;

              const eventDecay = heatwaveBonus + ddosBonus + networkBonus + powerBonus;

              let newSatisfaction = currentSatisfaction;
              if (isDown || eventDecay > 0) {
                // Down servers decay at full rate; event penalties stack on top
                const decayAmount = (isDown ? SATISFACTION_DECAY_PER_CHECK : 0) + eventDecay;
                newSatisfaction = Math.max(0, currentSatisfaction - decayAmount);
              } else {
                newSatisfaction = Math.min(100, currentSatisfaction + SATISFACTION_RECOVERY_PER_CHECK);
              }

              if (newSatisfaction !== currentSatisfaction) {
                s.satisfactionScore = newSatisfaction;
                changed = true;
              }

              // Customer churn: if satisfaction is below threshold, lose a customer
              if (churnThisTick && newSatisfaction < SATISFACTION_CHURN_THRESHOLD && (s.customers ?? 0) > 0) {
                s.customers = Math.max(0, (s.customers ?? 0) - 1);
                changed = true;
              }

              // ── Server Aging ─────────────────────────────────────────────
              if (agingThisTick && s.installedAt !== undefined) {
                const age = currentGameTime - s.installedAt;
                const currentEff = s.ageEfficiencyMultiplier ?? 1.0;

                let newStage: ServerAgeStage = s.ageStage ?? 'new';
                let newEff = currentEff;

                if (age >= SERVER_AGING_EOL_TICKS) {
                  newStage = 'eol';
                  newEff = Math.max(0.6, currentEff - SERVER_AGING_EFFICIENCY_DECAY);
                } else if (age >= SERVER_AGING_OLD_TICKS) {
                  newStage = 'aging';
                  newEff = Math.max(0.7, currentEff - SERVER_AGING_EFFICIENCY_DECAY);
                } else if (age >= SERVER_AGING_MATURE_TICKS) {
                  newStage = 'mature';
                  // Only start degrading past mature threshold
                  if (age > SERVER_AGING_MATURE_TICKS) {
                    newEff = Math.max(0.85, currentEff - SERVER_AGING_EFFICIENCY_DECAY);
                  }
                }

                const oldStage = s.ageStage ?? 'new';
                if (newStage !== oldStage || newEff !== currentEff) {
                  s.ageStage = newStage;
                  s.ageEfficiencyMultiplier = newEff;
                  changed = true;

                  if (newStage !== oldStage) {
                    const serverName = SERVER_TYPES.find(t => t.id === s.type)?.name ?? s.type;
                    if (newStage === 'eol') {
                      triggerNotification(`${serverName} in ${location.name} has reached End-of-Life! Efficiency greatly reduced.`, 'error', 8000);
                    } else if (newStage === 'aging') {
                      triggerNotification(`${serverName} in ${location.name} is aging — efficiency declining.`, 'warning', 6000);
                    }
                  }
                }
              }

              if (changed) {
                rackChanged = true;
                return s;
              }
              return server;
            });

            if (rackChanged) {
              floorChanged = true;
              return { ...rack, servers: updatedServers };
            }
            return rack;
          });

          if (floorChanged) {
            locChanged = true;
            updatedFloors[floorId] = { ...floor, racks: updatedRacks };
          } else {
            updatedFloors[floorId] = floor;
          }
        });

        if (locChanged) {
          anyChanged = true;
          next[locId] = { ...location, floors: updatedFloors };
        } else {
          next[locId] = location;
        }
      });

      return anyChanged ? next : prev;
    });
  }, [isPaused, setLocations, triggerNotification]);

  return { updateSatisfactionAndAging };
};
